/**
 * URL Utility Functions
 *
 * Utilities for validating and normalizing external URLs,
 * particularly for booking redirect URLs.
 */

/**
 * Normalize an external URL by ensuring it has a protocol
 *
 * @param url - URL to normalize
 * @returns Normalized URL with protocol (defaults to https://)
 *
 * @example
 * normalizeExternalUrl('www.google.com') // Returns: 'https://www.google.com'
 * normalizeExternalUrl('http://example.com') // Returns: 'http://example.com'
 * normalizeExternalUrl('https://example.com') // Returns: 'https://example.com'
 */
export const normalizeExternalUrl = (url: string): string => {
  if (!url) return url;

  const trimmed = url.trim();

  // Already has a protocol (http:// or https://)
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Add https:// by default for security
  return `https://${trimmed}`;
};

/**
 * Validate if a URL is properly formatted
 *
 * @param url - URL to validate
 * @returns true if valid URL format, false otherwise
 *
 * @example
 * isValidUrl('https://example.com') // Returns: true
 * isValidUrl('www.google.com') // Returns: true (will be normalized)
 * isValidUrl('not a url') // Returns: false
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || !url.trim()) return false;

  try {
    // Normalize first (adds https:// if missing)
    const normalized = normalizeExternalUrl(url);
    // Try to create URL object - will throw if invalid
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if a URL has a protocol (is absolute)
 *
 * @param url - URL to check
 * @returns true if URL has http:// or https://, false otherwise
 *
 * @example
 * isAbsoluteUrl('https://example.com') // Returns: true
 * isAbsoluteUrl('www.google.com') // Returns: false
 * isAbsoluteUrl('http://example.com') // Returns: true
 */
export const isAbsoluteUrl = (url: string): boolean => {
  if (!url) return false;
  return /^https?:\/\//i.test(url.trim());
};

/**
 * Post-login return URL
 *
 * When an unauthenticated user opens a deep link into a protected route
 * (e.g. the "Upgrade Now" CTA in a trial-expiry email pointing at
 * /profile?tab=plans), PrivateRoute bounces them to /login. These helpers
 * carry the intended destination across that bounce so sign-in lands them
 * where they were going instead of the default /schedule.
 *
 * sessionStorage (not localStorage) so the pending destination dies with
 * the tab and can't resurface in a later, unrelated session.
 */
const RETURN_URL_KEY = 'returnUrl';

/**
 * Check that a stored return URL is a safe same-origin relative path.
 *
 * Rejects absolute URLs and protocol-relative paths ("//evil.com"), which
 * the browser would treat as an off-site redirect. Only "/path" survives.
 */
export const isSafeReturnUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  // Must be root-relative, and must not be protocol-relative ("//host")
  // or a backslash variant ("/\host") that some browsers normalize to one.
  return /^\/(?![/\\])/.test(url);
};

/**
 * Remember where the user was headed before being sent to /login.
 * Silently ignores unsafe values rather than storing something we'd refuse to use later.
 */
export const setReturnUrl = (url: string): void => {
  if (!isSafeReturnUrl(url)) return;
  try {
    sessionStorage.setItem(RETURN_URL_KEY, url);
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall back to the default landing page
  }
};

/**
 * Read and clear the pending return URL.
 *
 * @returns the safe relative path, or null if none is pending or it failed validation
 */
export const consumeReturnUrl = (): string | null => {
  try {
    const stored = sessionStorage.getItem(RETURN_URL_KEY);
    if (!stored) return null;
    sessionStorage.removeItem(RETURN_URL_KEY);
    return isSafeReturnUrl(stored) ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Get a user-friendly domain from a URL
 *
 * @param url - URL to extract domain from
 * @returns Domain name without protocol, or original URL if invalid
 *
 * @example
 * getDomainFromUrl('https://www.google.com/path') // Returns: 'www.google.com'
 * getDomainFromUrl('www.example.com') // Returns: 'www.example.com'
 */
export const getDomainFromUrl = (url: string): string => {
  if (!url) return url;

  try {
    const normalized = normalizeExternalUrl(url);
    const urlObj = new URL(normalized);
    return urlObj.hostname;
  } catch {
    return url;
  }
};
